/**
 * Register the persistent top bar's left/right slots for a customer page.
 *
 * Left slot: plain two-row title + description, no background chip. The
 * workspace home has its own greeting-chip treatment; every other page
 * uses this hook for a consistent, minimal header. Right slot: page
 * actions.
 *
 * Usage:
 *   usePageHeader({
 *     title: t('users'),
 *     description: t('users-caption'),
 *     actions: <Button label={t('new-user')} onClick={openCreate} />
 *   });
 *
 * Any of the three fields may be omitted. The hook is a thin wrapper
 * around `useTopBarSlots` — see that file for the split-context internals
 * that keep the call site from re-rendering when the slots update.
 */

import { ReactNode } from 'react';
import { useTopBarSlots } from '@components/layout/top-bar-context';

interface Args {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}

export function usePageHeader({ title, description, actions }: Args): void {
  useTopBarSlots({
    left: title ? (
      <div className="flex flex-col min-w-0 max-w-full leading-tight">
        <span className="text-sm font-semibold truncate">{title}</span>
        {description && (
          <span className="text-sm font-semibold opacity-60 truncate">
            {description}
          </span>
        )}
      </div>
    ) : null,
    right: actions ?? null
  });
}
